import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  FolderOpen, File, Image, FilePdf, FileDoc, FileXls, Upload, Trash, FolderPlus,
  X, MagnifyingGlass, GridFour, ListBullets, CaretRight, CaretDown, DotsThree,
  ArrowsOut, Copy, FolderSimple, Check
} from '@phosphor-icons/react';

export default function CmsMediaLibrary() {
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [search, setSearch] = useState('');
  const [fileType, setFileType] = useState('all'); // 'all', 'image', 'document'
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [stats, setStats] = useState(null);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [expandedFolders, setExpandedFolders] = useState({});

  useEffect(() => {
    loadFolders();
    loadStats();
  }, []);

  useEffect(() => {
    loadFiles();
  }, [currentFolder, search, fileType]);

  const loadFolders = async () => {
    try {
      const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/media/folders`, {
        withCredentials: true,
      });
      setFolders(r.data.folders || []);
    } catch (e) {
      toast.error('Failed to load folders');
    }
  };

  const loadFiles = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (currentFolder) params.set('folder_id', currentFolder.folder_id);
      else params.set('folder_id', 'root');
      if (search) params.set('search', search);
      if (fileType !== 'all') params.set('file_type', fileType);
      params.set('limit', '100');

      const r = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/media/files?${params}`,
        { withCredentials: true }
      );
      setFiles(r.data.files || []);
    } catch (e) {
      toast.error('Failed to load files');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/media/stats`, {
        withCredentials: true,
      });
      setStats(r.data);
    } catch (e) {
      console.error('Failed to load stats');
    }
  };

  const createFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/media/folders`,
        {
          name: newFolderName,
          parent_id: currentFolder?.folder_id || null,
        },
        { withCredentials: true }
      );
      toast.success('Folder created');
      setNewFolderName('');
      setShowNewFolder(false);
      loadFolders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create folder');
    }
  };

  const deleteFolder = async (folderId) => {
    if (!window.confirm('Delete this folder? It must be empty.')) return;
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/admin/media/folders/${folderId}`, {
        withCredentials: true,
      });
      toast.success('Folder deleted');
      if (currentFolder?.folder_id === folderId) setCurrentFolder(null);
      loadFolders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete folder');
    }
  };

  const handleFileUpload = async (e) => {
    const fileList = e.target.files;
    if (!fileList || fileList.length === 0) return;

    setUploading(true);
    let successCount = 0;

    for (const file of fileList) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        if (currentFolder) formData.append('folder_id', currentFolder.folder_id);

        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/admin/media/upload`, formData, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        successCount++;
      } catch (e) {
        toast.error(`Failed to upload ${file.name}: ${e.response?.data?.detail || 'Error'}`);
      }
    }

    setUploading(false);
    if (successCount > 0) {
      toast.success(`Uploaded ${successCount} file(s)`);
      loadFiles();
      loadStats();
    }
    e.target.value = ''; // Reset input
  };

  const deleteFile = async (fileId) => {
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/admin/media/files/${fileId}`, {
        withCredentials: true,
      });
      toast.success('File deleted');
      loadFiles();
      loadStats();
      setSelectedFiles((prev) => prev.filter((id) => id !== fileId));
    } catch (e) {
      toast.error('Failed to delete file');
    }
  };

  const bulkDelete = async () => {
    if (!window.confirm(`Delete ${selectedFiles.length} selected file(s)?`)) return;
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/media/files/bulk-delete`,
        { file_ids: selectedFiles },
        { withCredentials: true }
      );
      toast.success(`Deleted ${selectedFiles.length} file(s)`);
      setSelectedFiles([]);
      loadFiles();
      loadStats();
    } catch (e) {
      toast.error('Bulk delete failed');
    }
  };

  const copyUrl = (filePath) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/files/${filePath}`;
    navigator.clipboard.writeText(url);
    toast.success('URL copied to clipboard');
  };

  const toggleFileSelection = (fileId) => {
    setSelectedFiles((prev) =>
      prev.includes(fileId) ? prev.filter((id) => id !== fileId) : [...prev, fileId]
    );
  };

  const toggleFolderExpand = (folderId) => {
    setExpandedFolders((prev) => ({ ...prev, [folderId]: !prev[folderId] }));
  };

  const renderFolderTree = (folderList, level = 0) => {
    return folderList.map((folder) => {
      const isExpanded = expandedFolders[folder.folder_id];
      const isCurrent = currentFolder?.folder_id === folder.folder_id;
      return (
        <div key={folder.folder_id} style={{ marginLeft: `${level * 12}px` }}>
          <div
            className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-sm ${
              isCurrent ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted'
            }`}
          >
            {folder.children?.length > 0 && (
              <button onClick={() => toggleFolderExpand(folder.folder_id)} className="p-0.5">
                {isExpanded ? <CaretDown size={14} /> : <CaretRight size={14} />}
              </button>
            )}
            <FolderSimple size={16} weight="duotone" />
            <span className="flex-1" onClick={() => setCurrentFolder(folder)}>
              {folder.name}
            </span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                deleteFolder(folder.folder_id);
              }}
              className="text-muted-foreground hover:text-red-600 p-0.5"
            >
              <Trash size={14} />
            </button>
          </div>
          {isExpanded && folder.children && renderFolderTree(folder.children, level + 1)}
        </div>
      );
    });
  };

  const getFileIcon = (file) => {
    if (file.is_image) return <Image size={20} weight="duotone" className="text-blue-600" />;
    if (file.content_type?.includes('pdf')) return <FilePdf size={20} weight="duotone" className="text-red-600" />;
    if (file.content_type?.includes('word')) return <FileDoc size={20} weight="duotone" className="text-blue-700" />;
    if (file.content_type?.includes('spreadsheet')) return <FileXls size={20} weight="duotone" className="text-green-600" />;
    return <File size={20} weight="duotone" className="text-muted-foreground" />;
  };

  return (
    <div className="space-y-6" data-testid="cms-media-library">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <FolderOpen size={28} weight="duotone" className="text-primary" />
            Media Library
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Manage images and documents with folders and compression
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="inline-flex items-center gap-1.5 px-5 py-2 bg-primary text-primary-foreground rounded-lg font-semibold text-sm cursor-pointer">
            <Upload size={16} /> Upload Files
            <input
              type="file"
              multiple
              accept="image/jpeg,image/png,image/webp,image/gif,.pdf,.docx,.xlsx"
              onChange={handleFileUpload}
              className="hidden"
              data-testid="file-upload-input"
            />
          </label>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Total Files</p>
            <p className="text-2xl font-bold">{stats.total_files}</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Images</p>
            <p className="text-2xl font-bold">{stats.total_images}</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Documents</p>
            <p className="text-2xl font-bold">{stats.total_documents}</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Storage Used</p>
            <p className="text-2xl font-bold">{stats.total_size_mb} MB</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-4 gap-6">
        {/* Sidebar - Folders */}
        <div className="col-span-1 bg-card border border-border rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-sm uppercase tracking-widest">Folders</h3>
            <button
              onClick={() => setShowNewFolder(true)}
              className="text-primary hover:text-primary/80"
              title="New Folder"
              data-testid="new-folder-btn"
            >
              <FolderPlus size={18} weight="duotone" />
            </button>
          </div>

          {showNewFolder && (
            <div className="mb-3 p-2 bg-muted rounded">
              <input
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && createFolder()}
                placeholder="Folder name"
                autoFocus
                className="w-full text-sm px-2 py-1 bg-background border border-border rounded mb-2"
                data-testid="new-folder-input"
              />
              <div className="flex gap-1">
                <button
                  onClick={createFolder}
                  className="flex-1 px-2 py-1 bg-primary text-primary-foreground rounded text-xs font-semibold"
                  data-testid="create-folder-btn"
                >
                  Create
                </button>
                <button
                  onClick={() => {
                    setShowNewFolder(false);
                    setNewFolderName('');
                  }}
                  className="flex-1 px-2 py-1 border border-border rounded text-xs"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          <div
            className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-sm mb-1 ${
              !currentFolder ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted'
            }`}
            onClick={() => setCurrentFolder(null)}
            data-testid="root-folder"
          >
            <FolderOpen size={16} weight="duotone" />
            <span>All Files (Root)</span>
          </div>

          <div className="space-y-0.5">{renderFolderTree(folders)}</div>
        </div>

        {/* Main Area - Files */}
        <div className="col-span-3 space-y-4">
          {/* Toolbar */}
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search files..."
                className="w-full pl-9 pr-3 py-2 text-sm bg-card border border-border rounded-lg"
                data-testid="search-input"
              />
            </div>
            <select
              value={fileType}
              onChange={(e) => setFileType(e.target.value)}
              className="px-3 py-2 text-sm bg-card border border-border rounded-lg"
              data-testid="file-type-filter"
            >
              <option value="all">All Types</option>
              <option value="image">Images</option>
              <option value="document">Documents</option>
            </select>
            <div className="flex items-center gap-1 border border-border rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-1.5 rounded ${viewMode === 'grid' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                data-testid="grid-view-btn"
              >
                <GridFour size={16} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-1.5 rounded ${viewMode === 'list' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                data-testid="list-view-btn"
              >
                <ListBullets size={16} />
              </button>
            </div>
            {selectedFiles.length > 0 && (
              <button
                onClick={bulkDelete}
                className="inline-flex items-center gap-1 px-3 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold"
                data-testid="bulk-delete-btn"
              >
                <Trash size={14} /> Delete ({selectedFiles.length})
              </button>
            )}
          </div>

          {/* Files Grid/List */}
          {uploading && <div className="text-center text-muted-foreground py-8">Uploading...</div>}
          {loading && !uploading && <div className="text-center text-muted-foreground py-8">Loading...</div>}
          {!loading && !uploading && files.length === 0 && (
            <div className="bg-card border border-border rounded-lg p-12 text-center">
              <FolderOpen size={48} weight="duotone" className="text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No files in this folder</p>
              <p className="text-sm text-muted-foreground mt-1">Upload files to get started</p>
            </div>
          )}

          {!loading && !uploading && files.length > 0 && (
            <>
              {viewMode === 'grid' ? (
                <div className="grid grid-cols-4 gap-4" data-testid="files-grid">
                  {files.map((file) => (
                    <div
                      key={file.file_id}
                      className={`bg-card border rounded-lg p-3 relative group ${
                        selectedFiles.includes(file.file_id) ? 'border-primary' : 'border-border'
                      }`}
                      data-testid={`file-${file.file_id}`}
                    >
                      <div className="absolute top-2 left-2">
                        <button
                          onClick={() => toggleFileSelection(file.file_id)}
                          className={`w-5 h-5 rounded border flex items-center justify-center ${
                            selectedFiles.includes(file.file_id)
                              ? 'bg-primary border-primary text-primary-foreground'
                              : 'bg-background border-border'
                          }`}
                        >
                          {selectedFiles.includes(file.file_id) && <Check size={12} weight="bold" />}
                        </button>
                      </div>
                      {file.is_image ? (
                        <img
                          src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${file.storage_path}`}
                          alt={file.filename}
                          className="w-full h-32 object-cover rounded mb-2"
                        />
                      ) : (
                        <div className="w-full h-32 flex items-center justify-center bg-muted rounded mb-2">
                          {getFileIcon(file)}
                        </div>
                      )}
                      <p className="text-xs font-medium truncate" title={file.filename}>
                        {file.filename}
                      </p>
                      <p className="text-[10px] text-muted-foreground">{(file.size / 1024).toFixed(1)} KB</p>
                      <div className="flex items-center gap-1 mt-2">
                        <button
                          onClick={() => copyUrl(file.storage_path)}
                          className="flex-1 p-1.5 bg-primary/10 text-primary rounded text-xs hover:bg-primary/20"
                          title="Copy URL"
                        >
                          <Copy size={12} className="mx-auto" />
                        </button>
                        <button
                          onClick={() => deleteFile(file.file_id)}
                          className="flex-1 p-1.5 bg-red-50 text-red-600 rounded text-xs hover:bg-red-100"
                          title="Delete"
                        >
                          <Trash size={12} className="mx-auto" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-card border border-border rounded-lg divide-y divide-border" data-testid="files-list">
                  {files.map((file) => (
                    <div
                      key={file.file_id}
                      className={`p-3 flex items-center gap-3 ${
                        selectedFiles.includes(file.file_id) ? 'bg-primary/5' : ''
                      }`}
                      data-testid={`file-${file.file_id}`}
                    >
                      <button
                        onClick={() => toggleFileSelection(file.file_id)}
                        className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 ${
                          selectedFiles.includes(file.file_id)
                            ? 'bg-primary border-primary text-primary-foreground'
                            : 'bg-background border-border'
                        }`}
                      >
                        {selectedFiles.includes(file.file_id) && <Check size={12} weight="bold" />}
                      </button>
                      {getFileIcon(file)}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{file.filename}</p>
                        <p className="text-xs text-muted-foreground">
                          {(file.size / 1024).toFixed(1)} KB · {new Date(file.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <button
                        onClick={() => copyUrl(file.storage_path)}
                        className="p-2 text-muted-foreground hover:text-primary"
                        title="Copy URL"
                      >
                        <Copy size={16} />
                      </button>
                      <button
                        onClick={() => deleteFile(file.file_id)}
                        className="p-2 text-muted-foreground hover:text-red-600"
                        title="Delete"
                      >
                        <Trash size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
